import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, TrendingUp, Clock, ArrowUp, ArrowDown, Minus, Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { apiClient, CloudSeverityTrend, CloudCategoryTrend } from '@/api/client';

interface CloudRiskTrendsTabProps {
  awsConnectionId?: string; // empty or undefined = all accounts
  ociConnectionId?: string; // empty or undefined = all accounts
}

export const CloudRiskTrendsTab: React.FC<CloudRiskTrendsTabProps> = ({ awsConnectionId, ociConnectionId }) => {
  const [trends, setTrends] = useState<CloudSeverityTrend[]>([]);
  const [categoryTrends, setCategoryTrends] = useState<CloudCategoryTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Date range state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();

  useEffect(() => {
    // Default to last 7 days
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);

    const endStr = end.toISOString().split('T')[0];
    const startStr = start.toISOString().split('T')[0];

    setStartDate(startStr);
    setEndDate(endStr);
    setCustomStartDate(start);
    setCustomEndDate(end);
  }, []);

  const loadRiskTrends = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.getCloudSeverityTrends({
        aws_connection_id: awsConnectionId || undefined,
        oci_connection_id: ociConnectionId || undefined,
        start_date: startDate,
        end_date: endDate
      });
      setTrends((response.trends as CloudSeverityTrend[]) || []);

      // Load category trends
      const categoryResponse = await apiClient.getCloudCategoryTrends({
        aws_connection_id: awsConnectionId || undefined,
        oci_connection_id: ociConnectionId || undefined,
        start_date: startDate,
        end_date: endDate
      });
      setCategoryTrends((categoryResponse.trends as CloudCategoryTrend[]) || []);
    } catch (err: any) {
      console.error('Failed to load cloud risk trends:', err);
      setError(err.message || 'Failed to load cloud risk trends');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, awsConnectionId, ociConnectionId]);

  useEffect(() => {
    if (startDate && endDate) {
      loadRiskTrends();
    }
  }, [startDate, endDate, awsConnectionId, ociConnectionId, loadRiskTrends]);

  // Quick preset functions
  const setLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const setLast30Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const setLast90Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 90);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const applyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setStartDate(format(customStartDate, 'yyyy-MM-dd'));
      setEndDate(format(customEndDate, 'yyyy-MM-dd'));
    }
  };

  // Format date for display (e.g., "Oct 09")
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };

  // Transform data for Recharts
  const chartData = trends.map(point => {
    const formattedDate = formatDate(point.date);
    return {
      date: formattedDate,
      fullDate: formattedDate,
      High: point.high_risk,
      Medium: point.medium_risk,
      Low: point.low_risk,
    };
  });

  // Calculate trend (first vs last data point)
  const calculateTrend = (type: 'high_risk' | 'medium_risk' | 'low_risk') => {
    if (trends.length < 2) return { change: 0, percentage: 0, direction: 'stable' as const, isNewFindings: false };

    const firstValue = trends[0][type];
    const lastValue = trends[trends.length - 1][type];
    const change = lastValue - firstValue;

    let percentage = 0;
    let isNewFindings = false;

    if (firstValue === 0 && change !== 0) {
      isNewFindings = true;
      percentage = 0;
    } else if (firstValue !== 0) {
      percentage = Math.abs((change / firstValue) * 100);
    }

    let direction: 'up' | 'down' | 'stable' = 'stable';
    if (change > 0) direction = 'up';
    else if (change < 0) direction = 'down';

    return { change, percentage, direction, isNewFindings };
  };

  const highTrend = calculateTrend('high_risk');
  const mediumTrend = calculateTrend('medium_risk');
  const lowTrend = calculateTrend('low_risk');

  // Calculate trend for category
  const calculateCategoryTrend = (type: keyof CloudCategoryTrend) => {
    if (type === 'date' || categoryTrends.length < 2) return { change: 0, percentage: 0, direction: 'stable' as const, isNewFindings: false };

    const firstValue = categoryTrends[0][type] as number || 0;
    const lastValue = categoryTrends[categoryTrends.length - 1][type] as number || 0;
    const change = lastValue - firstValue;

    let percentage = 0;
    let isNewFindings = false;

    if (firstValue === 0 && change !== 0) {
      isNewFindings = true;
      percentage = 0;
    } else if (firstValue !== 0) {
      percentage = Math.abs((change / firstValue) * 100);
    }

    let direction: 'up' | 'down' | 'stable' = 'stable';
    if (change > 0) direction = 'up';
    else if (change < 0) direction = 'down';

    return { change, percentage, direction, isNewFindings };
  };

  const accessControlTrend = calculateCategoryTrend('access_control');
  const credentialSecurityTrend = calculateCategoryTrend('credential_security');
  const policySecurityTrend = calculateCategoryTrend('policy_security');
  const complianceTrend = calculateCategoryTrend('compliance');
  const networkTrend = calculateCategoryTrend('network_security');
  const dataProtectionTrend = calculateCategoryTrend('data_protection');

  // Render trend indicator
  const TrendIndicator = ({ trend }: { trend: ReturnType<typeof calculateTrend> }) => {
    if (trend.direction === 'stable' || trend.change === 0) {
      return (
        <div className="flex items-center gap-1 text-gray-500">
          <Minus className="h-4 w-4" />
          <span className="text-sm font-medium">No change</span>
        </div>
      );
    }

    const isImproving = trend.change < 0;
    const color = isImproving ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    const bgColor = isImproving ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20';
    const Icon = trend.direction === 'down' ? ArrowDown : ArrowUp;

    if (trend.isNewFindings) {
      return (
        <div className={`flex items-center gap-1 ${color}`}>
          <div className={`p-1 rounded ${bgColor}`}>
            <Icon className="h-3 w-3" />
          </div>
          <span className="text-sm font-medium">
            +{Math.abs(trend.change)}
          </span>
          <span className="text-xs">
            {isImproving ? 'improved' : 'increased'}
          </span>
          <span className="text-xs text-muted-foreground">
            (from 0)
          </span>
        </div>
      );
    }

    return (
      <div className={`flex items-center gap-1 ${color}`}>
        <div className={`p-1 rounded ${bgColor}`}>
          <Icon className="h-3 w-3" />
        </div>
        <span className="text-sm font-medium">
          {Math.abs(trend.change)} ({trend.percentage.toFixed(1)}%)
        </span>
        <span className="text-xs">
          {isImproving ? 'improved' : 'increased'}
        </span>
      </div>
    );
  };

  // Custom tooltip for severity
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-medium text-sm mb-2">{data.fullDate}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-700 dark:text-gray-300">High: {data.High}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Medium: {data.Medium}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Low: {data.Low}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for categories
  const CategoryTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          <p className="font-medium text-sm mb-2">{data.date}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Access Control: {data.access_control || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Credential Security: {data.credential_security || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Policy Security: {data.policy_security || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Compliance: {data.compliance || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Network Security: {data.network_security || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
              <span className="text-gray-700 dark:text-gray-300">Data Protection: {data.data_protection || 0}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <Clock className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Loading cloud risk trends...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Error Loading Trends</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={loadRiskTrends}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trends.length === 0) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <TrendingUp className="h-16 w-16 text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2 text-foreground">No Trend Data Available</h3>
            <p className="text-muted-foreground max-w-md">
              No cloud security findings found in the selected time period. Run cloud security assessments on your AWS accounts to see trends over time.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-foreground" />
          <span className="text-sm font-medium">Time Period:</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={setLast7Days}>
            7 Days
          </Button>
          <Button variant="outline" size="sm" onClick={setLast30Days}>
            30 Days
          </Button>
          <Button variant="outline" size="sm" onClick={setLast90Days}>
            90 Days
          </Button>
        </div>

        <div className="h-6 w-px bg-border" />

        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customStartDate ? format(customStartDate, 'MMM dd, yyyy') : 'Start date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customStartDate}
                onSelect={setCustomStartDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <span className="text-sm text-muted-foreground">to</span>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customEndDate ? format(customEndDate, 'MMM dd, yyyy') : 'End date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customEndDate}
                onSelect={setCustomEndDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button size="sm" onClick={applyCustomRange}>
            Apply
          </Button>
        </div>
      </div>

      {/* Tabs for Severity vs Category View */}
      <Tabs defaultValue="severity" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="severity">By Severity</TabsTrigger>
          <TabsTrigger value="category">By Category</TabsTrigger>
        </TabsList>

        {/* Severity Graph */}
        <TabsContent value="severity">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <CardTitle>Cloud Risk Findings Over Time</CardTitle>
              </div>
              <CardDescription>
                Hover over data points to see detailed counts for each severity level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      className="text-sm"
                      tick={{ fill: 'currentColor', dy: 8 }}
                    />
                    <YAxis
                      className="text-sm"
                      tick={{ fill: 'currentColor' }}
                      label={{ value: 'Number of Findings', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="High"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ fill: '#ef4444', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="High Risk"
                    />
                    <Line
                      type="monotone"
                      dataKey="Medium"
                      stroke="#f97316"
                      strokeWidth={2}
                      dot={{ fill: '#f97316', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Medium Risk"
                    />
                    <Line
                      type="monotone"
                      dataKey="Low"
                      stroke="#eab308"
                      strokeWidth={2}
                      dot={{ fill: '#eab308', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Low Risk"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Summary Statistics */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <p className="text-sm font-medium text-muted-foreground">High Risk Findings</p>
                  </div>
                  <p className="text-2xl font-bold mb-2">
                    {trends[trends.length - 1].high_risk}
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                  <div className="pt-2 border-t">
                    <TrendIndicator trend={highTrend} />
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <p className="text-sm font-medium text-muted-foreground">Medium Risk Findings</p>
                  </div>
                  <p className="text-2xl font-bold mb-2">
                    {trends[trends.length - 1].medium_risk}
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                  <div className="pt-2 border-t">
                    <TrendIndicator trend={mediumTrend} />
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <p className="text-sm font-medium text-muted-foreground">Low Risk Findings</p>
                  </div>
                  <p className="text-2xl font-bold mb-2">
                    {trends[trends.length - 1].low_risk}
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                  <div className="pt-2 border-t">
                    <TrendIndicator trend={lowTrend} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Category Graph */}
        <TabsContent value="category">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <CardTitle>Findings by Category Over Time</CardTitle>
              </div>
              <CardDescription>
                Track how different security categories trend over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={categoryTrends}
                    margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      className="text-sm"
                      tick={{ fill: 'currentColor', dy: 8 }}
                    />
                    <YAxis
                      className="text-sm"
                      tick={{ fill: 'currentColor' }}
                      label={{ value: 'Number of Findings', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip content={<CategoryTooltip />} />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="access_control"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      name="Access Control"
                    />
                    <Line
                      type="monotone"
                      dataKey="credential_security"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ fill: '#ef4444', r: 4 }}
                      name="Credential Security"
                    />
                    <Line
                      type="monotone"
                      dataKey="policy_security"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ fill: '#8b5cf6', r: 4 }}
                      name="Policy Security"
                    />
                    <Line
                      type="monotone"
                      dataKey="compliance"
                      stroke="#f97316"
                      strokeWidth={2}
                      dot={{ fill: '#f97316', r: 4 }}
                      name="Compliance"
                    />
                    <Line
                      type="monotone"
                      dataKey="network_security"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: '#10b981', r: 4 }}
                      name="Network Security"
                    />
                    <Line
                      type="monotone"
                      dataKey="data_protection"
                      stroke="#06b6d4"
                      strokeWidth={2}
                      dot={{ fill: '#06b6d4', r: 4 }}
                      name="Data Protection"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Summary Statistics for Categories */}
              {categoryTrends.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Access Control</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].access_control || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={accessControlTrend} />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Credential Security</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].credential_security || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={credentialSecurityTrend} />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Policy Security</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].policy_security || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={policySecurityTrend} />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Compliance</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].compliance || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={complianceTrend} />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Network Security</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].network_security || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={networkTrend} />
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      <p className="text-sm font-medium text-muted-foreground">Data Protection</p>
                    </div>
                    <p className="text-2xl font-bold mb-2">
                      {categoryTrends[categoryTrends.length - 1].data_protection || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">Current findings</p>
                    <div className="pt-2 border-t">
                      <TrendIndicator trend={dataProtectionTrend} />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CloudRiskTrendsTab;
